import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { app_permissions } from '../../../security/app-permissions';
import { PermissionService } from '../../../security/permission.service';
import { BankReconciliationService } from '../bank-reconciliation.service';
import {
    BankReconciliationAuditEvent,
    BankReconciliationAdjustment,
    BankReconciliationAdjustmentType,
    BankReconciliationAutoMatchResponse,
    BankReconciliationCandidate,
    BankReconciliationException,
    BankReconciliationExceptionType,
    BankReconciliationMatchDetail,
    BankReconciliationMatchSuggestion,
    BankReconciliationWorkspace,
    BankStatementLine,
    BankStatementLineStatus
} from '../models/bank-reconciliation.model';

interface MatchBasketItem {
    candidate: BankReconciliationCandidate;
    matchedAmount: number;
}

@Component({
    selector: 'app-bank-reconciliation-workspace',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, DialogModule, SelectModule, InputNumberModule, InputTextModule, TableModule, TagModule, TextareaModule,
        PageShellComponent, PageHeroComponent, PageMetricStripComponent, PageDataPanelComponent],
    template: `
        <app-page-shell>
            <app-page-hero eyebrow="Manual Reconciliation" [title]="title" [subtitle]="subtitle">
                <div actions>
                <button pButton type="button" icon="pi pi-list" label="Statement" [outlined]="true" (click)="openStatement()"></button>
                <button pButton type="button" icon="pi pi-chart-bar" label="Report" [outlined]="true" (click)="openReport()"></button>
                <button pButton type="button" icon="pi pi-send" label="Submit" [outlined]="true" *ngIf="canLifecycleAction('SUBMIT')" [loading]="savingLifecycle" (click)="transitionSession('SUBMIT')"></button>
                    <button pButton type="button" icon="pi pi-check-circle" label="Approve" [outlined]="true" *ngIf="canLifecycleAction('APPROVE')" [loading]="savingLifecycle" (click)="transitionSession('APPROVE')"></button>
                    <button pButton type="button" icon="pi pi-lock" label="Close" [outlined]="true" *ngIf="canLifecycleAction('CLOSE')" [loading]="savingLifecycle" (click)="transitionSession('CLOSE')"></button>
                    <button pButton type="button" icon="pi pi-lock-open" label="Reopen" [outlined]="true" *ngIf="canLifecycleAction('REOPEN')" [loading]="savingLifecycle" (click)="openLifecycleDialog('REOPEN')"></button>
                    <button pButton type="button" icon="pi pi-ban" label="Cancel" [outlined]="true" severity="danger" *ngIf="canLifecycleAction('CANCEL')" [loading]="savingLifecycle" (click)="openLifecycleDialog('CANCEL')"></button>
                    <button pButton type="button" icon="pi pi-wand-magic-sparkles" label="Auto-Match Preview" [outlined]="true" [loading]="loadingAutoMatch" (click)="previewAutoMatch()"></button>
                    <button pButton type="button" icon="pi pi-sliders-h" label="Rules" [outlined]="true" (click)="openRules()"></button>
                    <button pButton type="button" icon="pi pi-refresh" label="Refresh" (click)="refreshAll()"></button>
                </div>
            </app-page-hero>

            <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

            <app-page-data-panel title="Lifecycle Control" [resultText]="workspace?.session?.status || 'Loading'">
                <div class="lifecycle-grid">
                    <div><span>Status</span><strong>{{ workspace?.session?.status || '-' }}</strong></div>
                    <div><span>Variance</span><strong>{{ workspace?.totals?.variance | number:'1.2-4' }}</strong></div>
                    <div><span>Tolerance</span><strong>{{ workspace?.varianceTolerance | number:'1.2-4' }}</strong></div>
                    <div><span>Open exceptions</span><strong>{{ workspace?.hasOpenExceptions ? 'Yes' : 'No' }}</strong></div>
                    <div><span>Unresolved lines</span><strong>{{ workspace?.unmatchedLineCount || 0 }}</strong></div>
                    <div><span>Locked lines</span><strong>{{ workspace?.lockedLineCount || 0 }}</strong></div>
                </div>
                <div class="blocked-list" *ngIf="blockedReasonEntries.length">
                    <div class="blocked-row" *ngFor="let item of blockedReasonEntries">
                        <strong>{{ item[0] }}</strong>
                        <span>{{ item[1] }}</span>
                    </div>
                </div>
            </app-page-data-panel>

            <app-page-data-panel *ngIf="autoMatchResponse" title="Auto-Match Preview"
                                 [resultText]="autoMatchResponse.suggestedCount + ' suggestions from ' + autoMatchResponse.scannedLineCount + ' scanned lines'">
                <div actions class="toolbar">
                    <button pButton type="button" icon="pi pi-check-circle" label="Apply Safe Matches" [loading]="applyingAutoMatch"
                            [disabled]="!safeSuggestionCount" (click)="applyAutoMatch()"></button>
                </div>
                <p-table [value]="autoMatchResponse.suggestions" styleClass="p-datatable-sm">
                    <ng-template pTemplate="header">
                        <tr><th>Bank Line</th><th>Candidate</th><th>Amount</th><th>Confidence</th><th>Rule</th><th>Status</th></tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-suggestion>
                        <tr>
                            <td>
                                <strong>#{{ suggestion.statementLine?.lineNumber || suggestion.statementLineId }}</strong>
                                <div class="muted">{{ suggestion.statementLine?.bankReference || suggestion.statementLine?.description || '-' }}</div>
                            </td>
                            <td>
                                <strong>{{ suggestion.candidate?.sourceNumber || suggestion.candidate?.entryNumber || ('JI-' + suggestion.journalItemId) }}</strong>
                                <div class="muted">{{ suggestion.candidate?.reference || suggestion.candidate?.name || '-' }}</div>
                            </td>
                            <td>{{ suggestion.amount | number:'1.2-2' }}</td>
                            <td>{{ suggestion.confidenceScore | number:'1.2-4' }}</td>
                            <td>
                                {{ suggestion.ruleName || 'Default' }}
                                <div class="reason-codes">{{ (suggestion.reasonCodes || []).join(', ') }}</div>
                            </td>
                            <td><p-tag [value]="suggestionStatus(suggestion)" [severity]="suggestionSeverity(suggestion)"></p-tag></td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="6">No suggestions found for current open lines.</td></tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>

            <section class="workspace-grid">
                <app-page-data-panel title="Bank Lines" [resultText]="bankLines.length + ' lines'">
                    <div actions class="toolbar">
                        <input pInputText placeholder="Reference or description" [(ngModel)]="lineSearchText" (keyup.enter)="loadLines()">
                        <button pButton type="button" icon="pi pi-search" label="Search" [outlined]="true" (click)="loadLines()"></button>
                    </div>
                    <p-table [value]="bankLines" [loading]="loadingLines" styleClass="p-datatable-sm bank-lines" [scrollable]="true" scrollHeight="520px">
                        <ng-template pTemplate="header">
                            <tr><th>Line</th><th>Amount</th><th>Status</th></tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-line>
                            <tr [class.selected-row]="selectedLine?.statementLineId === line.statementLineId" (click)="selectLine(line)">
                                <td>
                                    <strong>{{ line.transactionDate }}</strong>
                                    <div class="muted">{{ line.bankReference || line.customerReference || 'No reference' }}</div>
                                    <div class="description">{{ line.description || '-' }}</div>
                                </td>
                                <td>
                                    <strong>{{ line.signedAmount | number:'1.2-2' }}</strong>
                                    <div class="muted">Open {{ lineOpenAmount(line) | number:'1.2-2' }}</div>
                                </td>
                                <td>
                                    <p-tag [value]="line.status" [severity]="lineSeverity(line.status)"></p-tag>
                                    <p-tag *ngIf="line.reconciliationLocked" value="LOCKED" severity="danger"></p-tag>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="3">No bank lines match the current filters.</td></tr>
                        </ng-template>
                    </p-table>
                </app-page-data-panel>

                <app-page-data-panel title="Book Candidates" [resultText]="candidateCount + ' open items'">
                    <div actions class="toolbar">
                        <input pInputText placeholder="Entry, reference, receipt, voucher" [(ngModel)]="candidateSearchText" (keyup.enter)="loadCandidates()">
                        <button pButton type="button" icon="pi pi-search" label="Search" [outlined]="true" (click)="loadCandidates()"></button>
                    </div>
                    <p-table [value]="candidates" [loading]="loadingCandidates" styleClass="p-datatable-sm" [scrollable]="true" scrollHeight="520px">
                        <ng-template pTemplate="header">
                            <tr><th>Source</th><th>Amount</th><th></th></tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-candidate>
                            <tr>
                                <td>
                                    <strong>{{ candidate.sourceType }}</strong>
                                    <div class="muted">{{ candidate.sourceNumber || candidate.entryNumber || ('JI-' + candidate.journalItemId) }}</div>
                                    <div class="description">{{ candidate.reference || candidate.name || '-' }}</div>
                                </td>
                                <td>
                                    <strong>{{ candidate.openAmount | number:'1.2-2' }}</strong>
                                    <div class="muted">{{ candidate.entryDate || '-' }} {{ candidate.currencyCode || '' }}</div>
                                </td>
                                <td class="actions">
                                    <button pButton type="button" icon="pi pi-plus" label="Add" [text]="true" [disabled]="!selectedLine || isCandidateInBasket(candidate)" (click)="addCandidate(candidate)"></button>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="3">No open book-side candidates found.</td></tr>
                        </ng-template>
                    </p-table>
                </app-page-data-panel>

                <app-page-data-panel title="Match Basket" [resultText]="basket.length + ' selected'">
                    <div class="selected-line" *ngIf="selectedLine; else noLine">
                        <strong>Bank line {{ selectedLine.lineNumber }}</strong>
                        <span>{{ selectedLine.transactionDate }} | {{ selectedLine.description || selectedLine.bankReference || '-' }}</span>
                        <p-tag [value]="selectedLine.status" [severity]="lineSeverity(selectedLine.status)"></p-tag>
                        <div class="toolbar">
                            <button pButton type="button" icon="pi pi-plus-circle" label="Create Adjustment" [outlined]="true" [disabled]="selectedLine.reconciliationLocked" (click)="openAdjustmentDialog()"></button>
                            <button pButton type="button" icon="pi pi-exclamation-triangle" label="Raise Exception" [outlined]="true" severity="warn" [disabled]="selectedLine.reconciliationLocked" (click)="openExceptionDialog()"></button>
                        </div>
                    </div>
                    <ng-template #noLine>
                        <div class="empty-state">Select a bank line to start matching.</div>
                    </ng-template>

                    <div class="basket" *ngIf="basket.length">
                        <div class="basket-row" *ngFor="let item of basket">
                            <div>
                                <strong>{{ item.candidate.sourceNumber || item.candidate.entryNumber || ('JI-' + item.candidate.journalItemId) }}</strong>
                                <div class="muted">Open {{ item.candidate.openAmount | number:'1.2-2' }}</div>
                            </div>
                            <p-inputNumber [(ngModel)]="item.matchedAmount" mode="decimal" [min]="0" [max]="item.candidate.openAmount" [minFractionDigits]="2" [maxFractionDigits]="4"></p-inputNumber>
                            <button pButton type="button" icon="pi pi-times" [text]="true" severity="danger" (click)="removeCandidate(item)"></button>
                        </div>
                    </div>

                    <textarea pTextarea rows="3" placeholder="Match notes" [(ngModel)]="comment"></textarea>

                    <div class="totals">
                        <div><span>Bank open</span><strong>{{ selectedLineOpenAmount | number:'1.2-2' }}</strong></div>
                        <div><span>Selected book</span><strong>{{ basketTotal | number:'1.2-2' }}</strong></div>
                        <div><span>Variance</span><strong [class.invalid]="basketVariance < 0">{{ basketVariance | number:'1.2-2' }}</strong></div>
                    </div>

                    <button pButton type="button" icon="pi pi-check" label="Create Match" class="full-width"
                            [disabled]="!canCreateMatch()" [loading]="savingMatch" (click)="createMatch()"></button>

                    <h4>Match History</h4>
                    <p-table [value]="selectedLineMatches" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header">
                            <tr><th>Match</th><th>Amount</th><th>Status</th><th></th></tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-match>
                            <tr>
                                <td>#{{ match.match.matchId }}</td>
                                <td>{{ match.match.matchedAmount | number:'1.2-2' }}</td>
                                <td><p-tag [value]="match.match.matchStatus" [severity]="match.match.matchStatus === 'MATCHED' ? 'success' : 'info'"></p-tag></td>
                                <td class="actions"><button pButton type="button" icon="pi pi-undo" label="Unmatch" [text]="true" severity="danger" (click)="unmatch(match)"></button></td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="4">No active matches for this line.</td></tr>
                        </ng-template>
                    </p-table>

                    <h4>Adjustments</h4>
                    <p-table [value]="selectedLineAdjustments" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header">
                            <tr><th>Type</th><th>Amount</th><th>Status</th><th></th></tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-adjustment>
                            <tr>
                                <td>
                                    {{ adjustment.adjustmentType }}
                                    <div class="muted" *ngIf="adjustment.journalEntryId">JE #{{ adjustment.journalEntryId }}</div>
                                </td>
                                <td>{{ adjustment.amount | number:'1.2-2' }}</td>
                                <td><p-tag [value]="adjustment.status" [severity]="adjustmentSeverity(adjustment.status)"></p-tag></td>
                                <td class="actions">
                                    <button pButton type="button" icon="pi pi-check" label="Approve" [text]="true" *ngIf="adjustment.status === 'DRAFT'" (click)="approveAdjustment(adjustment)"></button>
                                    <button pButton type="button" icon="pi pi-times" label="Cancel" [text]="true" severity="danger" *ngIf="adjustment.status === 'DRAFT'" (click)="cancelAdjustment(adjustment)"></button>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="4">No adjustments for this line.</td></tr>
                        </ng-template>
                    </p-table>

                    <h4>Exceptions</h4>
                    <p-table [value]="selectedLineExceptions" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header">
                            <tr><th>Type</th><th>Severity</th><th>Status</th><th></th></tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-exception>
                            <tr>
                                <td>
                                    {{ exception.exceptionType }}
                                    <div class="muted">{{ exception.description || '-' }}</div>
                                </td>
                                <td>{{ exception.severity }}</td>
                                <td><p-tag [value]="exception.status" [severity]="exceptionSeverity(exception.status)"></p-tag></td>
                                <td class="actions">
                                    <button pButton type="button" icon="pi pi-eye" label="Review" [text]="true" *ngIf="exception.status === 'OPEN'" (click)="reviewException(exception)"></button>
                                    <button pButton type="button" icon="pi pi-check" label="Resolve" [text]="true" *ngIf="exception.status === 'OPEN' || exception.status === 'IN_REVIEW'" (click)="resolveException(exception)"></button>
                                    <button pButton type="button" icon="pi pi-ban" label="Exclude" [text]="true" severity="danger" *ngIf="exception.status === 'OPEN' || exception.status === 'IN_REVIEW'" (click)="excludeException(exception)"></button>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="4">No exceptions for this line.</td></tr>
                        </ng-template>
                    </p-table>

                    <h4>Audit Timeline</h4>
                    <div class="audit-list">
                        <div class="audit-row" *ngFor="let event of auditEvents">
                            <strong>{{ event.action }}</strong>
                            <span>{{ event.performedAt || '-' }}</span>
                            <p>{{ event.comment || statusChange(event) }}</p>
                        </div>
                    </div>
                </app-page-data-panel>
            </section>

            <p-dialog header="Create Reconciliation Adjustment" [(visible)]="adjustmentDialogVisible" [modal]="true" [style]="{ width: '520px' }">
                <div class="dialog-grid">
                    <label>Adjustment type</label>
                    <p-select [options]="adjustmentTypeOptions" [(ngModel)]="adjustmentForm.adjustmentType" optionLabel="label" optionValue="value"></p-select>
                    <label>Amount</label>
                    <p-inputNumber [(ngModel)]="adjustmentForm.amount" mode="decimal" [min]="0" [max]="selectedLineOpenAmount" [minFractionDigits]="2" [maxFractionDigits]="4"></p-inputNumber>
                    <label>Offset account id</label>
                    <p-inputNumber [(ngModel)]="adjustmentForm.accountId" [useGrouping]="false"></p-inputNumber>
                    <label>Notes</label>
                    <textarea pTextarea rows="3" [(ngModel)]="adjustmentForm.notes"></textarea>
                    <div class="muted span-2">Bank line open amount: {{ selectedLineOpenAmount | number:'1.2-2' }}</div>
                </div>
                <ng-template pTemplate="footer">
                    <button pButton type="button" label="Cancel" [outlined]="true" (click)="adjustmentDialogVisible = false"></button>
                    <button pButton type="button" label="Save Draft" [disabled]="!canSaveAdjustment()" [loading]="savingAdjustment" (click)="saveAdjustment()"></button>
                </ng-template>
            </p-dialog>

            <p-dialog header="Raise Reconciliation Exception" [(visible)]="exceptionDialogVisible" [modal]="true" [style]="{ width: '520px' }">
                <div class="dialog-grid">
                    <label>Exception type</label>
                    <p-select [options]="exceptionTypeOptions" [(ngModel)]="exceptionForm.exceptionType" optionLabel="label" optionValue="value"></p-select>
                    <label>Severity</label>
                    <p-select [options]="severityOptions" [(ngModel)]="exceptionForm.severity"></p-select>
                    <label>Description</label>
                    <textarea pTextarea rows="4" [(ngModel)]="exceptionForm.description"></textarea>
                </div>
                <ng-template pTemplate="footer">
                    <button pButton type="button" label="Cancel" [outlined]="true" (click)="exceptionDialogVisible = false"></button>
                    <button pButton type="button" label="Raise Exception" [disabled]="!canSaveException()" [loading]="savingException" (click)="saveException()"></button>
                </ng-template>
            </p-dialog>

            <p-dialog [header]="lifecycleAction === 'REOPEN' ? 'Reopen Reconciliation Session' : 'Cancel Reconciliation Session'"
                      [(visible)]="lifecycleDialogVisible" [modal]="true" [style]="{ width: '520px' }">
                <div class="dialog-grid">
                    <label>{{ lifecycleAction === 'REOPEN' ? 'Reason' : 'Comment' }}</label>
                    <textarea pTextarea rows="4" [(ngModel)]="lifecycleComment"></textarea>
                </div>
                <ng-template pTemplate="footer">
                    <button pButton type="button" label="Back" [outlined]="true" (click)="lifecycleDialogVisible = false"></button>
                    <button pButton type="button" [label]="lifecycleAction === 'REOPEN' ? 'Reopen' : 'Cancel Session'"
                            [disabled]="!lifecycleComment" [loading]="savingLifecycle" (click)="confirmLifecycleDialog()"></button>
                </ng-template>
            </p-dialog>
        </app-page-shell>
    `,
    styles: [`
        .workspace-grid { display: grid; grid-template-columns: 1.05fr 1.05fr .95fr; gap: 1rem; align-items: start; }
        .toolbar { display: flex; gap: .5rem; flex-wrap: wrap; }
        .toolbar input { min-width: 220px; }
        .selected-row { background: #ecfeff !important; }
        .bank-lines tr { cursor: pointer; }
        .muted { color: #64748b; font-size: .78rem; margin-top: .2rem; }
        .description { color: #334155; font-size: .82rem; margin-top: .25rem; max-width: 280px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .actions { text-align: right; }
        .selected-line { display: grid; gap: .25rem; padding: .75rem; border: 1px solid #dbeafe; border-radius: .8rem; background: #f8fafc; margin-bottom: .75rem; }
        .empty-state { padding: 1rem; color: #64748b; border: 1px dashed #cbd5e1; border-radius: .8rem; margin-bottom: .75rem; }
        .basket { display: grid; gap: .5rem; margin-bottom: .75rem; }
        .basket-row { display: grid; grid-template-columns: 1fr 120px auto; gap: .5rem; align-items: center; padding: .65rem; border: 1px solid #e2e8f0; border-radius: .8rem; }
        textarea { width: 100%; margin: .75rem 0; }
        .totals { display: grid; grid-template-columns: repeat(3, 1fr); gap: .5rem; margin: .75rem 0; }
        .totals div { display: grid; gap: .2rem; padding: .65rem; background: #f8fafc; border-radius: .75rem; }
        .totals span { color: #64748b; font-size: .78rem; }
        .invalid { color: #dc2626; }
        .full-width { width: 100%; justify-content: center; }
        h4 { margin: 1rem 0 .5rem; }
        .audit-list { display: grid; gap: .5rem; max-height: 260px; overflow: auto; }
        .audit-row { border-left: 3px solid #0f766e; padding: .35rem .65rem; background: #f8fafc; border-radius: .35rem; }
        .audit-row span { display: block; color: #64748b; font-size: .75rem; }
        .audit-row p { margin: .2rem 0 0; color: #334155; }
        .reason-codes { color: #64748b; font-size: .75rem; margin-top: .25rem; }
        .dialog-grid { display: grid; grid-template-columns: 150px 1fr; gap: .75rem; align-items: center; }
        .dialog-grid label { font-weight: 600; color: #334155; }
        .dialog-grid textarea, .dialog-grid p-select, .dialog-grid p-inputnumber { width: 100%; }
        .span-2 { grid-column: 1 / -1; }
        .lifecycle-grid { display: grid; grid-template-columns: repeat(6, minmax(110px, 1fr)); gap: .75rem; }
        .lifecycle-grid div { display: grid; gap: .2rem; padding: .75rem; border-radius: .8rem; background: #f8fafc; border: 1px solid #e2e8f0; }
        .lifecycle-grid span { color: #64748b; font-size: .78rem; }
        .blocked-list { display: grid; gap: .4rem; margin-top: .75rem; }
        .blocked-row { display: flex; gap: .5rem; align-items: center; padding: .5rem .75rem; border-radius: .7rem; background: #fff7ed; color: #9a3412; }
        @media (max-width: 1200px) { .workspace-grid { grid-template-columns: 1fr; } }
        @media (max-width: 1200px) { .lifecycle-grid { grid-template-columns: repeat(2, 1fr); } }
    `]
})
export class BankReconciliationWorkspaceComponent implements OnInit {
    sessionId = 0;
    workspace?: BankReconciliationWorkspace;
    bankLines: BankStatementLine[] = [];
    candidates: BankReconciliationCandidate[] = [];
    candidateCount = 0;
    selectedLine?: BankStatementLine;
    basket: MatchBasketItem[] = [];
    auditEvents: BankReconciliationAuditEvent[] = [];
    comment = '';
    lineSearchText = '';
    candidateSearchText = '';
    loadingLines = false;
    loadingCandidates = false;
    savingMatch = false;
    loadingAutoMatch = false;
    applyingAutoMatch = false;
    autoMatchResponse?: BankReconciliationAutoMatchResponse;
    metrics: PageMetric[] = [];
    adjustmentDialogVisible = false;
    exceptionDialogVisible = false;
    savingAdjustment = false;
    savingException = false;
    savingLifecycle = false;
    lifecycleDialogVisible = false;
    lifecycleAction: 'REOPEN' | 'CANCEL' = 'REOPEN';
    lifecycleComment = '';
    adjustmentForm: { adjustmentType: BankReconciliationAdjustmentType; amount: number; accountId?: number; notes?: string } = {
        adjustmentType: 'BANK_FEE',
        amount: 0
    };
    exceptionForm: { exceptionType: BankReconciliationExceptionType; severity: string; description?: string } = {
        exceptionType: 'UNMATCHED_BANK_LINE',
        severity: 'MEDIUM'
    };
    adjustmentTypeOptions = [
        { label: 'Bank fee', value: 'BANK_FEE' },
        { label: 'Interest income', value: 'INTEREST_INCOME' },
        { label: 'Interest expense', value: 'INTEREST_EXPENSE' },
        { label: 'Write-off', value: 'WRITE_OFF' },
        { label: 'Suspense receipt', value: 'SUSPENSE_RECEIPT' },
        { label: 'Suspense payment', value: 'SUSPENSE_PAYMENT' },
        { label: 'FX difference', value: 'FX_DIFFERENCE' }
    ];
    exceptionTypeOptions = [
        { label: 'Unmatched bank line', value: 'UNMATCHED_BANK_LINE' },
        { label: 'Missing book transaction', value: 'MISSING_BOOK_TRANSACTION' },
        { label: 'Missing bank transaction', value: 'MISSING_BANK_TRANSACTION' },
        { label: 'Duplicate bank line', value: 'DUPLICATE_BANK_LINE' },
        { label: 'Amount mismatch', value: 'AMOUNT_MISMATCH' },
        { label: 'Date mismatch', value: 'DATE_MISMATCH' },
        { label: 'Bank charge', value: 'BANK_CHARGE' },
        { label: 'Interest', value: 'INTEREST' },
        { label: 'FX difference', value: 'FX_DIFFERENCE' },
        { label: 'Unknown receipt', value: 'UNKNOWN_RECEIPT' },
        { label: 'Unknown payment', value: 'UNKNOWN_PAYMENT' }
    ];
    severityOptions = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

    constructor(
        private route: ActivatedRoute,
        private bankReconciliationService: BankReconciliationService,
        private sessionStorageService: SessionStorageService,
        private permissionService: PermissionService,
        private router: Router
    ) {}

    get title(): string {
        return this.workspace?.statement?.statementNumber || `Reconciliation Session ${this.sessionId}`;
    }

    get subtitle(): string {
        const session = this.workspace?.session;
        if (!session) return 'Loading reconciliation workspace.';
        return `${session.periodStartDate || '-'} to ${session.periodEndDate || '-'} | ${session.status}`;
    }

    get selectedLineOpenAmount(): number {
        return this.selectedLine ? this.lineOpenAmount(this.selectedLine) : 0;
    }

    get basketTotal(): number {
        return this.round(this.basket.reduce((total, item) => total + Number(item.matchedAmount || 0), 0));
    }

    get basketVariance(): number {
        return this.round(this.selectedLineOpenAmount - this.basketTotal);
    }

    get selectedLineMatches(): BankReconciliationMatchDetail[] {
        if (!this.selectedLine || !this.workspace) return [];
        return (this.workspace.activeMatches ?? []).filter(match => match.match.statementLineId === this.selectedLine?.statementLineId);
    }

    get selectedLineAdjustments(): BankReconciliationAdjustment[] {
        if (!this.selectedLine || !this.workspace) return [];
        return (this.workspace.adjustments ?? []).filter(adjustment => adjustment.statementLineId === this.selectedLine?.statementLineId);
    }

    get selectedLineExceptions(): BankReconciliationException[] {
        if (!this.selectedLine || !this.workspace) return [];
        return (this.workspace.exceptions ?? []).filter(exception => exception.statementLineId === this.selectedLine?.statementLineId);
    }

    get safeSuggestionCount(): number {
        return (this.autoMatchResponse?.suggestions ?? []).filter(suggestion => !suggestion.ambiguous && !suggestion.blockedReason).length;
    }

    get blockedReasonEntries(): [string, string][] {
        return Object.entries(this.workspace?.blockedReasons ?? {});
    }

    ngOnInit(): void {
        this.sessionId = Number(this.route.snapshot.paramMap.get('sessionId'));
        this.refreshAll();
    }

    refreshAll(): void {
        this.loadWorkspace();
    }

    loadWorkspace(): void {
        this.bankReconciliationService.getWorkspace(this.sessionId).subscribe(workspace => {
            this.workspace = workspace;
            this.auditEvents = workspace.recentAuditEvents ?? [];
            this.metrics = [
                { label: 'Bank open', value: this.amount(workspace.totals?.bankOpenAmount), tone: 'warn' },
                { label: 'Book open', value: this.amount(workspace.totals?.bookOpenAmount), tone: 'info' },
                { label: 'Matched lines', value: workspace.totals?.matchedLines ?? 0, tone: 'success' },
                { label: 'Variance', value: this.amount(workspace.totals?.variance), tone: Math.abs(workspace.totals?.variance ?? 0) > (workspace.varianceTolerance ?? 0.01) ? 'danger' : 'success' },
                { label: 'Locked lines', value: workspace.lockedLineCount ?? 0, tone: workspace.lockedLineCount ? 'danger' : 'default' }
            ];
            this.loadLines();
            this.loadCandidates();
        });
    }

    loadLines(): void {
        const statementId = this.workspace?.statement?.statementId;
        if (!statementId) return;
        this.loadingLines = true;
        this.bankReconciliationService.getStatementLines(statementId, { searchText: this.lineSearchText, page: 0, size: 500 }).subscribe({
            next: response => {
                this.bankLines = response.items ?? [];
                if (!this.selectedLine) {
                    this.selectLine(this.bankLines.find(line => line.status === 'UNMATCHED' || line.status === 'PARTIAL') ?? this.bankLines[0]);
                } else {
                    this.selectedLine = this.bankLines.find(line => line.statementLineId === this.selectedLine?.statementLineId);
                }
                this.loadingLines = false;
            },
            error: () => this.loadingLines = false
        });
    }

    loadCandidates(): void {
        this.loadingCandidates = true;
        this.bankReconciliationService.searchSessionCandidates(this.sessionId, {
            searchText: this.candidateSearchText,
            page: 0,
            size: 100
        }).subscribe({
            next: response => {
                this.candidates = response.items ?? [];
                this.candidateCount = response.count ?? 0;
                this.loadingCandidates = false;
            },
            error: () => this.loadingCandidates = false
        });
    }

    selectLine(line?: BankStatementLine): void {
        this.selectedLine = line;
        this.basket = [];
        this.comment = '';
    }

    addCandidate(candidate: BankReconciliationCandidate): void {
        if (!this.selectedLine || this.isCandidateInBasket(candidate)) return;
        const amount = Math.min(this.selectedLineOpenAmount - this.basketTotal, candidate.openAmount);
        this.basket.push({ candidate, matchedAmount: this.round(Math.max(amount, 0)) });
    }

    removeCandidate(item: MatchBasketItem): void {
        this.basket = this.basket.filter(row => row !== item);
    }

    isCandidateInBasket(candidate: BankReconciliationCandidate): boolean {
        return this.basket.some(item => item.candidate.journalItemId === candidate.journalItemId);
    }

    canCreateMatch(): boolean {
        if (!this.selectedLine || !this.basket.length || this.savingMatch) return false;
        if (this.selectedLine.reconciliationLocked) return false;
        if (this.basketTotal <= 0 || this.basketTotal > this.selectedLineOpenAmount) return false;
        return this.basket.every(item => item.matchedAmount > 0 && item.matchedAmount <= item.candidate.openAmount);
    }

    createMatch(): void {
        if (!this.selectedLine || !this.canCreateMatch()) return;
        this.savingMatch = true;
        this.bankReconciliationService.createMatch(this.sessionId, {
            statementLineId: this.selectedLine.statementLineId,
            matchType: 'MANUAL',
            matchedAmount: this.basketTotal,
            comment: this.comment,
            performedBy: this.sessionStorageService.getCustomer()?.id,
            items: this.basket.map(item => ({
                sourceType: item.candidate.sourceType,
                sourceId: item.candidate.sourceId ?? item.candidate.journalItemId,
                journalItemId: item.candidate.journalItemId,
                sourceNumber: item.candidate.sourceNumber,
                reference: item.candidate.reference,
                entryDate: item.candidate.entryDate,
                signedAmount: item.candidate.signedAmount,
                matchedAmount: item.matchedAmount
            }))
        }).subscribe({
            next: () => {
                this.savingMatch = false;
                this.selectedLine = undefined;
                this.basket = [];
                this.comment = '';
                this.refreshAll();
            },
            error: () => this.savingMatch = false
        });
    }

    unmatch(match: BankReconciliationMatchDetail): void {
        this.bankReconciliationService.deleteMatch(this.sessionId, match.match.matchId, this.sessionStorageService.getCustomer()?.id).subscribe(() => {
            this.selectedLine = undefined;
            this.refreshAll();
        });
    }

    previewAutoMatch(): void {
        this.loadingAutoMatch = true;
        this.bankReconciliationService.autoMatch(this.sessionId, false, this.sessionStorageService.getCustomer()?.id).subscribe({
            next: response => {
                this.autoMatchResponse = response;
                this.loadingAutoMatch = false;
            },
            error: () => this.loadingAutoMatch = false
        });
    }

    applyAutoMatch(): void {
        this.applyingAutoMatch = true;
        this.bankReconciliationService.autoMatch(this.sessionId, true, this.sessionStorageService.getCustomer()?.id).subscribe({
            next: response => {
                this.autoMatchResponse = response;
                this.applyingAutoMatch = false;
                this.selectedLine = undefined;
                this.refreshAll();
            },
            error: () => this.applyingAutoMatch = false
        });
    }

    openAdjustmentDialog(): void {
        if (!this.selectedLine || this.selectedLine.reconciliationLocked) return;
        this.adjustmentForm = {
            adjustmentType: this.selectedLine.signedAmount < 0 ? 'BANK_FEE' : 'INTEREST_INCOME',
            amount: this.selectedLineOpenAmount,
            notes: this.selectedLine.description || this.selectedLine.bankReference
        };
        this.adjustmentDialogVisible = true;
    }

    canSaveAdjustment(): boolean {
        return !!this.selectedLine && !this.selectedLine.reconciliationLocked && Number(this.adjustmentForm.amount || 0) > 0
            && Number(this.adjustmentForm.amount || 0) <= this.selectedLineOpenAmount;
    }

    saveAdjustment(): void {
        if (!this.selectedLine || !this.canSaveAdjustment()) return;
        this.savingAdjustment = true;
        this.bankReconciliationService.createAdjustment(this.sessionId, {
            statementLineId: this.selectedLine.statementLineId,
            adjustmentType: this.adjustmentForm.adjustmentType,
            amount: this.adjustmentForm.amount,
            accountId: this.adjustmentForm.accountId,
            notes: this.adjustmentForm.notes,
            createdBy: this.sessionStorageService.getCustomer()?.id
        }).subscribe({
            next: () => {
                this.savingAdjustment = false;
                this.adjustmentDialogVisible = false;
                this.refreshAll();
            },
            error: () => this.savingAdjustment = false
        });
    }

    approveAdjustment(adjustment: BankReconciliationAdjustment): void {
        this.bankReconciliationService.approveAdjustment(this.sessionId, adjustment.adjustmentId, {
            performedBy: this.sessionStorageService.getCustomer()?.id,
            comment: 'Approved from reconciliation workspace'
        }).subscribe(() => {
            this.selectedLine = undefined;
            this.refreshAll();
        });
    }

    cancelAdjustment(adjustment: BankReconciliationAdjustment): void {
        this.bankReconciliationService.cancelAdjustment(this.sessionId, adjustment.adjustmentId, {
            performedBy: this.sessionStorageService.getCustomer()?.id,
            comment: 'Cancelled from reconciliation workspace'
        }).subscribe(() => this.refreshAll());
    }

    openExceptionDialog(): void {
        if (!this.selectedLine || this.selectedLine.reconciliationLocked) return;
        this.exceptionForm = {
            exceptionType: 'UNMATCHED_BANK_LINE',
            severity: 'MEDIUM',
            description: this.selectedLine.description || this.selectedLine.bankReference
        };
        this.exceptionDialogVisible = true;
    }

    canSaveException(): boolean {
        return !!this.selectedLine && !this.selectedLine.reconciliationLocked && !!this.exceptionForm.exceptionType && !!this.exceptionForm.severity;
    }

    saveException(): void {
        if (!this.selectedLine || !this.canSaveException()) return;
        this.savingException = true;
        this.bankReconciliationService.createException(this.sessionId, {
            sessionId: this.sessionId,
            statementLineId: this.selectedLine.statementLineId,
            exceptionType: this.exceptionForm.exceptionType,
            severity: this.exceptionForm.severity,
            description: this.exceptionForm.description,
            performedBy: this.sessionStorageService.getCustomer()?.id
        }).subscribe({
            next: () => {
                this.savingException = false;
                this.exceptionDialogVisible = false;
                this.refreshAll();
            },
            error: () => this.savingException = false
        });
    }

    reviewException(exception: BankReconciliationException): void {
        this.bankReconciliationService.reviewException(exception.exceptionId, {
            performedBy: this.sessionStorageService.getCustomer()?.id,
            comment: 'Marked in review from reconciliation workspace'
        }).subscribe(() => this.refreshAll());
    }

    resolveException(exception: BankReconciliationException): void {
        this.bankReconciliationService.resolveException(exception.exceptionId, {
            performedBy: this.sessionStorageService.getCustomer()?.id,
            resolutionNotes: 'Resolved from reconciliation workspace'
        }).subscribe(() => this.refreshAll());
    }

    excludeException(exception: BankReconciliationException): void {
        this.bankReconciliationService.excludeException(exception.exceptionId, {
            performedBy: this.sessionStorageService.getCustomer()?.id,
            resolutionNotes: 'Excluded from reconciliation workspace'
        }).subscribe(() => {
            this.selectedLine = undefined;
            this.refreshAll();
        });
    }

    canLifecycleAction(action: 'SUBMIT' | 'APPROVE' | 'CLOSE' | 'REOPEN' | 'CANCEL'): boolean {
        const permissionMap = {
            SUBMIT: app_permissions.accounts.bank_reconciliation.submit,
            APPROVE: app_permissions.accounts.bank_reconciliation.approve,
            CLOSE: app_permissions.accounts.bank_reconciliation.close,
            REOPEN: app_permissions.accounts.bank_reconciliation.reopen,
            CANCEL: app_permissions.accounts.bank_reconciliation.cancel
        };
        return (this.workspace?.allowedActions ?? []).includes(action)
            && this.permissionService.hasPermission(permissionMap[action]);
    }

    transitionSession(action: 'SUBMIT' | 'APPROVE' | 'CLOSE'): void {
        this.savingLifecycle = true;
        const request = {
            performedBy: this.sessionStorageService.getCustomer()?.id,
            comment: `${action} from reconciliation workspace`
        };
        const request$ = action === 'SUBMIT'
            ? this.bankReconciliationService.submitSession(this.sessionId, request)
            : action === 'APPROVE'
                ? this.bankReconciliationService.approveSession(this.sessionId, request)
                : this.bankReconciliationService.closeSession(this.sessionId, request);
        request$.subscribe({
            next: () => {
                this.savingLifecycle = false;
                this.selectedLine = undefined;
                this.refreshAll();
            },
            error: () => this.savingLifecycle = false
        });
    }

    openLifecycleDialog(action: 'REOPEN' | 'CANCEL'): void {
        this.lifecycleAction = action;
        this.lifecycleComment = '';
        this.lifecycleDialogVisible = true;
    }

    confirmLifecycleDialog(): void {
        if (!this.lifecycleComment) return;
        this.savingLifecycle = true;
        const performedBy = this.sessionStorageService.getCustomer()?.id;
        const request$ = this.lifecycleAction === 'REOPEN'
            ? this.bankReconciliationService.reopenSession(this.sessionId, {
                performedBy,
                reason: this.lifecycleComment,
                comment: this.lifecycleComment
            })
            : this.bankReconciliationService.cancelSession(this.sessionId, {
                performedBy,
                comment: this.lifecycleComment
            });
        request$.subscribe({
            next: () => {
                this.savingLifecycle = false;
                this.lifecycleDialogVisible = false;
                this.selectedLine = undefined;
                this.refreshAll();
            },
            error: () => this.savingLifecycle = false
        });
    }

    openStatement(): void {
        const statementId = this.workspace?.statement?.statementId;
        if (statementId) {
            this.router.navigate(['/accounts/enterprise/bank-reconciliation/statements', statementId]);
        }
    }

    openRules(): void {
        this.router.navigate(['/accounts/enterprise/bank-reconciliation/rules']);
    }

    openReport(): void {
        this.router.navigate(['/accounts/enterprise/bank-reconciliation/reports/session', this.sessionId]);
    }

    lineOpenAmount(line: BankStatementLine): number {
        return this.round(Math.abs(Number(line.signedAmount || 0)) - Number(line.matchedAmount || 0));
    }

    lineSeverity(status: BankStatementLineStatus): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'MATCHED') return 'success';
        if (status === 'PARTIAL') return 'info';
        if (status === 'EXCEPTION' || status === 'EXCLUDED') return 'danger';
        return 'warn';
    }

    statusChange(event: BankReconciliationAuditEvent): string {
        if (event.beforeStatus || event.afterStatus) return `${event.beforeStatus || '-'} -> ${event.afterStatus || '-'}`;
        return '';
    }

    suggestionStatus(suggestion: BankReconciliationMatchSuggestion): string {
        if (suggestion.applied) return 'Applied';
        if (suggestion.ambiguous) return 'Ambiguous';
        if (suggestion.blockedReason?.includes('threshold') || suggestion.blockedReason?.includes('confidence')) return 'Below Threshold';
        if (suggestion.blockedReason) return 'Blocked';
        return 'Ready';
    }

    suggestionSeverity(suggestion: BankReconciliationMatchSuggestion): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const status = this.suggestionStatus(suggestion);
        if (status === 'Applied' || status === 'Ready') return 'success';
        if (status === 'Ambiguous' || status === 'Below Threshold') return 'warn';
        if (status === 'Blocked') return 'danger';
        return 'info';
    }

    adjustmentSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'POSTED') return 'success';
        if (status === 'APPROVED') return 'info';
        if (status === 'CANCELLED') return 'danger';
        return 'warn';
    }

    exceptionSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'RESOLVED') return 'success';
        if (status === 'IN_REVIEW') return 'info';
        if (status === 'EXCLUDED') return 'danger';
        return 'warn';
    }

    private amount(value?: number): string {
        return `${this.round(value ?? 0)}`;
    }

    private round(value: number): number {
        return Math.round((Number(value || 0) + Number.EPSILON) * 10000) / 10000;
    }
}
